import { formatDistanceToNow } from 'date-fns';
import {
  Edit,
  Eye,
  Lock,
  Mail,
  MailX,
  MoreHorizontal,
  Unlock,
  UserCheck,
  UserX,
} from 'lucide-react';
import { Link } from 'react-router';
import { User } from '@/services/admin/user.service';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface UsersTableProps {
  data: User[];
  isLoading: boolean;
}

export function UsersTable({ data, isLoading }: UsersTableProps) {
  if (isLoading) {
    return (
      <div className="p-6 space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="p-12 text-center">
        <div className="text-muted-foreground">
          <p className="text-lg font-medium">No users found</p>
          <p className="text-sm mt-1">Try adjusting your search or filters</p>
        </div>
      </div>
    );
  }

  const isUserLocked = (user: User) => {
    return user.lockoutEnd && new Date(user.lockoutEnd) > new Date();
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>User</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Roles</TableHead>
          <TableHead>Created</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((user) => (
          <TableRow key={user.id}>
            {/* User Info */}
            <TableCell>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-medium text-primary">
                    {user.name?.charAt(0).toUpperCase() ||
                      user.userName?.charAt(0).toUpperCase() ||
                      '?'}
                    {user.surname?.charAt(0).toUpperCase() || ''}
                  </span>
                </div>
                <div>
                  <Link
                    to={`/users/${user.id}`}
                    className="font-medium hover:underline"
                  >
                    {user.name || user.userName} {user.surname || ''}
                  </Link>
                  <div className="text-sm text-muted-foreground">
                    @{user.userName}
                  </div>
                </div>
              </div>
            </TableCell>

            {/* Email */}
            <TableCell>
              <div className="flex items-center gap-2">
                <span className="text-sm">{user.email}</span>
                {user.emailConfirmed ? (
                  <Mail className="h-3 w-3 text-green-600" />
                ) : (
                  <MailX className="h-3 w-3 text-orange-600" />
                )}
              </div>
            </TableCell>

            {/* Status */}
            <TableCell>
              <div className="flex flex-col gap-1">
                {isUserLocked(user) ? (
                  <Badge variant="destructive" className="w-fit">
                    <Lock className="h-3 w-3 mr-1" />
                    Locked
                  </Badge>
                ) : user.isActive ? (
                  <Badge variant="success" className="w-fit">
                    <UserCheck className="h-3 w-3 mr-1" />
                    Active
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="w-fit">
                    <UserX className="h-3 w-3 mr-1" />
                    Inactive
                  </Badge>
                )}
              </div>
            </TableCell>

            {/* Roles */}
            <TableCell>
              <div className="flex flex-wrap gap-1">
                {user.roles && user.roles.length > 0 ? (
                  user.roles.slice(0, 2).map((role, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {role}
                    </Badge>
                  ))
                ) : (
                  <span className="text-sm text-muted-foreground">
                    No roles
                  </span>
                )}
                {user.roles && user.roles.length > 2 && (
                  <Badge variant="outline" className="text-xs">
                    +{user.roles.length - 2}
                  </Badge>
                )}
              </div>
            </TableCell>

            {/* Created */}
            <TableCell>
              <div className="text-sm text-muted-foreground">
                {formatDistanceToNow(new Date(user.creationTime), {
                  addSuffix: true,
                })}
              </div>
            </TableCell>

            {/* Actions */}
            <TableCell className="text-right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link
                      to={`/users/${user.id}`}
                      className="flex items-center cursor-pointer"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit User
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {isUserLocked(user) ? (
                    <DropdownMenuItem>
                      <Unlock className="h-4 w-4 mr-2" />
                      Unlock Account
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem>
                      <Lock className="h-4 w-4 mr-2" />
                      Lock Account
                    </DropdownMenuItem>
                  )}
                  {user.isActive ? (
                    <DropdownMenuItem>
                      <UserX className="h-4 w-4 mr-2" />
                      Deactivate
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem>
                      <UserCheck className="h-4 w-4 mr-2" />
                      Activate
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
